import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Loader2, ShieldX } from "lucide-react";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import { AuthLayout } from "../../../auth";
import { isCenterInvitationValid } from "../../application/centerSignUp";
import CenterSignUpForm from "../components/CenterSignUpForm";

type Gate = "checking" | "open" | "closed";

/**
 * A centre's own front door.
 *
 * Separate from `/signup` because the fields differ, the copy differs, and the
 * two audiences arrive from different places — not because the plumbing does.
 *
 * **Open to anybody.** A centre is a product that is sold: the administrator
 * signs up, then buys the seats they need and the modules those seats carry.
 *
 * A code is still honoured, and it is worth one thing — a centre that never
 * pays. One that does not work is refused rather than ignored: somebody
 * holding a code believes they were given something, and quietly signing them
 * up as a paying centre instead is the worst of the three outcomes.
 */
export default function CenterSignUpPage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const invitationToken = searchParams.get("invite") ?? undefined;
    // No code is the ordinary case now, and it opens the form straight away.
    const [gate, setGate] = useState<Gate>(invitationToken ? "checking" : "open");

    useEffect(() => {
        if (!invitationToken) {
            setGate("open");
            return;
        }
        setGate("checking");
        isCenterInvitationValid(invitationToken)
            .then((valid) => setGate(valid ? "open" : "closed"))
            .catch(() => setGate("closed"));
    }, [invitationToken]);

    return (
        <>
            <PageMeta />
            <AuthLayout panelTitle={t("center.signUp.panelTitle")}>
                {gate === "checking" && (
                    <div className="flex flex-col items-center gap-4 py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
                        <p className="text-sm text-content-tertiary">{t("invite.validating")}</p>
                    </div>
                )}
                {gate === "closed" && (
                    <div className="flex flex-col items-center gap-4 py-8 text-center">
                        <ShieldX className="h-12 w-12 text-content-tertiary" aria-hidden="true" />
                        <h1 className="text-xl font-semibold text-content-primary">
                            {t("center.signUp.inviteInvalidTitle")}
                        </h1>
                        <p className="max-w-sm text-sm text-content-tertiary">
                            {t("center.signUp.inviteInvalidDescription")}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("center.signUp.soloPrompt")}{" "}
                            <Link to="/signup" className="text-brand-600 hover:underline">
                                {t("center.signUp.soloLink")}
                            </Link>
                        </p>
                    </div>
                )}
                {gate === "open" && <CenterSignUpForm invitationToken={invitationToken} />}
            </AuthLayout>
        </>
    );
}
